import React from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
  Quote,
  Link as LinkIcon,
  Type,
  Highlighter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ColorPicker from './ColorPicker';
import FontFamilySelect from './FontFamilySelect';
import FontSizeSelect from './FontSizeSelect';
import ImageUploadButton from './ImageUploadButton';

interface EditorToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor, disabled = false }) => {
  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertImage = (url: string) => {
    editor.chain().focus().setImage({ src: url }).run();
  };

  const currentFontFamily = editor.getAttributes('textStyle').fontFamily;
  const currentFontSize = editor.getAttributes('textStyle').fontSize;
  const currentColor = editor.getAttributes('textStyle').color;
  const currentHighlight = editor.getAttributes('highlight').color;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/30">
      {/* Font Family */}
      <FontFamilySelect
        value={currentFontFamily}
        onChange={(font) => editor.chain().focus().setFontFamily(font).run()}
        disabled={disabled}
      />

      {/* Font Size */}
      <FontSizeSelect
        value={currentFontSize}
        onChange={(size) => editor.chain().focus().setFontSize(size).run()}
        disabled={disabled}
      />

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Text Formatting */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('bold') && 'bg-muted')}
        disabled={disabled}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('italic') && 'bg-muted')}
        disabled={disabled}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('underline') && 'bg-muted')}
        disabled={disabled}
        title="Underline"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('strike') && 'bg-muted')}
        disabled={disabled}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Colors */}
      <ColorPicker
        value={currentColor}
        onChange={(color) => {
          if (color) {
            editor.chain().focus().setColor(color).run();
          } else {
            editor.chain().focus().unsetColor().run();
          }
        }}
        icon={<Type className="h-4 w-4" />}
        tooltip="Text color"
        disabled={disabled}
        isActive={!!currentColor}
      />
      <ColorPicker
        value={currentHighlight}
        onChange={(color) => {
          if (color) {
            editor.chain().focus().setHighlight({ color }).run();
          } else {
            editor.chain().focus().unsetHighlight().run();
          }
        }}
        icon={<Highlighter className="h-4 w-4" />}
        tooltip="Highlight color"
        disabled={disabled}
        isActive={!!currentHighlight}
      />

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Alignment */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'left' }) && 'bg-muted')}
        disabled={disabled}
        title="Align left"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'center' }) && 'bg-muted')}
        disabled={disabled}
        title="Align center"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'right' }) && 'bg-muted')}
        disabled={disabled}
        title="Align right"
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'justify' }) && 'bg-muted')}
        disabled={disabled}
        title="Justify"
      >
        <AlignJustify className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('bulletList') && 'bg-muted')}
        disabled={disabled}
        title="Bullet list"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('orderedList') && 'bg-muted')}
        disabled={disabled}
        title="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        className="h-8 w-8 p-0"
        disabled={disabled || !editor.can().sinkListItem('listItem')}
        title="Increase indent"
      >
        <IndentIncrease className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        className="h-8 w-8 p-0"
        disabled={disabled || !editor.can().liftListItem('listItem')}
        title="Decrease indent"
      >
        <IndentDecrease className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Block & Insert */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('blockquote') && 'bg-muted')}
        disabled={disabled}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addLink}
        className={cn('h-8 w-8 p-0', editor.isActive('link') && 'bg-muted')}
        disabled={disabled}
        title="Insert link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <ImageUploadButton onInsert={insertImage} disabled={disabled} />
    </div>
  );
};

export default EditorToolbar;
